import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subject } from 'rxjs';
import BrowserHelper from 'src/app/helpers/browser-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import DateHelper from 'src/app/helpers/date-helper';
import EmployeeHelper from 'src/app/helpers/employee-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { NotExecutedHistoryItem } from 'src/app/model/history-item/not-executed-history-item';
import Employee from 'src/app/model/product/employee';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import { Project } from 'src/app/model/project/project';
import { Repo } from 'src/app/model/project/repo';
import { SoldRented } from 'src/app/model/project/sold-rented';
import {
  SubProject,
  SUB_PROJECT_TYPES,
} from 'src/app/model/project/sub-project';
import { User } from 'src/app/model/user';
import { SubProjectTourInfo } from 'src/app/pages/projects/helpers/ip-board-sub-project-tour-selector/helper/model/sub-project-tour-info';
import { SubProjectTourInfoControllerRequestService } from 'src/app/pages/projects/helpers/ip-board-sub-project-tour-selector/service/sub-project-tour-info-controller-request.service';
import { GetMostRecentNotExecutedHistoryItemControllerRequestService } from 'src/app/services/api/history-item/get-most-recent-not-executed-history-item-controller-request.service';
import { AuthService } from 'src/app/services/auth.service';
import { BranchChoicesService } from 'src/app/services/branch/branch-choices.service';
import { ModalService } from 'src/app/services/modal.service';
import {
  ProjectDisplayObservableService,
  TourConfigurationChangedEvent,
} from 'src/app/services/project-display-observable.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { AssignSubProjectToTourControllerRequestService } from '../../../pages/immo-ip/tour/tour-page/service/assign-sub-project-to-tour-controller-request.service';

@Component({
  selector: 'execution-collapse',
  templateUrl: './execution-collapse.component.html',
  styleUrls: ['./execution-collapse.component.scss'],
})
export class ExecutionCollapseComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnDestroy
{
  @Input()
  project: Project;

  @Input()
  subProject: SubProject;

  @Input()
  subProjectTitle: string;

  @Input()
  client: boolean = true;

  @Output()
  public save = new EventEmitter();

  @Input()
  divergentPricesOnly = false;

  tourConfigurationDisplay: string;
  placer: Employee;
  executionCollapsed = true;
  safari: boolean;

  notExecutedHistoryItem: NotExecutedHistoryItem;
  tourUnassigned: Subject<void> = new Subject<void>();
  isRepoNotByInpress: boolean = false;
  isNotInstalledByInpress: boolean = false;

  firstProductBoardPart: IPBoardPart;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private subProjectTourInfoControllerRequestService: SubProjectTourInfoControllerRequestService,
    private getMostRecentNotExecutedHistoryItemControllerRequestService: GetMostRecentNotExecutedHistoryItemControllerRequestService,
    private assignSubProjectToTourControllerRequestService: AssignSubProjectToTourControllerRequestService,
    private branchChoicesService: BranchChoicesService,
    private modalService: ModalService,
    private authService: AuthService
  ) {
    super();
  }

  ngOnInit(): void {
    this.safari = BrowserHelper.isSafari();

    if (
      this.project != null &&
      this.project.products.length > 0 &&
      ProductHelper.isIPBoard(this.project.products[0])
    ) {
      this.firstProductBoardPart = (<IPBoard>(
        this.project.products[0]
      )).boardPart;
    }

    if (this.client && this.subProject.id != null) {
      this.subProjectTourInfoControllerRequestService
        .subProjectTourInfo(this.subProject.id)
        .then((subProjectTourInfo: SubProjectTourInfo) => {
          if (subProjectTourInfo != null && subProjectTourInfo.assignedToTour) {
            this.tourConfigurationDisplay = StringHelper.join(
              [
                DateHelper.formatDate(subProjectTourInfo.date, 'dd/MM/yyyy'),
                EmployeeHelper.getName(subProjectTourInfo.placer),
              ],
              ' - '
            );
          }
        });
    }

    this.addSubscription(
      this.projectDisplayObservableService.getTourConfigurationChangedSubscribable(),
      (event: TourConfigurationChangedEvent) => {
        if (event.subProjectId === this.subProject.id) {
          this.placer = event.placer;
          this.tourConfigurationDisplay = event.assignedToTour
            ? StringHelper.join(
                [
                  DateHelper.formatDate(event.date, 'dd/MM/yyyy'),
                  EmployeeHelper.getName(event.placer),
                ],
                ' - '
              )
            : null;
        }
      }
    );

    if (!this.client && this.subProject.id != null) {
      this.isRepoNotByInpress =
        this.subProject.type === SUB_PROJECT_TYPES.REPO &&
        !(this.subProject as Repo).repoByInpress;
      this.isNotInstalledByInpress =
        this.subProject.type === SUB_PROJECT_TYPES.SOLDRENTED &&
        !(this.subProject as SoldRented).installByInpress;
      this.getNotExecutedHistoryItem();
      this.branchChoicesService.updateBranchChoices();

      const executed = ProjectHelper.getSubProjectNullableExecutionState(
        this.subProject,
        this.project
      );

      const ipboardProducts = this.project.products.filter((x) =>
        ProductHelper.isIPBoard(x)
      );

      const hasExecutionAssets =
        this.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT
          ? CollectionHelper.isPopulated(ipboardProducts) &&
            CollectionHelper.isPopulated(ipboardProducts[0].productAssets) &&
            ipboardProducts[0].productAssets.some(
              (a) => !a.archived && a.formSection === 'execution'
            )
          : this.subProject.type !== SUB_PROJECT_TYPES.REPO &&
            CollectionHelper.isPopulated(
              this.subProject.projectPlacement.projectPlacementAssets
            ) &&
            this.subProject.projectPlacement.projectPlacementAssets.some(
              (a) =>
                !a.archived && a.formSection === 'adjustmentPlacementSuccess'
            );

      if (!this.isNotInstalledByInpress || !this.isRepoNotByInpress) {
        this.executionCollapsed = true;
      } else if (this.subProject.delayed || executed || hasExecutionAssets) {
        this.executionCollapsed = false;
      }
    }

    this.addSubscription(
      this.projectDisplayObservableService.getRepoByInpressSubscribable(),
      (event: number) => {
        if (event === this.subProject.id) {
          this.isRepoNotByInpress =
            this.subProject.type === SUB_PROJECT_TYPES.REPO &&
            !(this.subProject as Repo).repoByInpress;
          this.executionCollapsed = !this.isRepoNotByInpress
            ? true
            : this.executionCollapsed;
        }
      }
    );

    this.addSubscription(
      this.projectDisplayObservableService.getRepoByInpressSubscribable(),
      (event: number) => {
        if (event === this.subProject.id) {
          this.isNotInstalledByInpress =
            this.subProject.type === SUB_PROJECT_TYPES.SOLDRENTED &&
            !(this.subProject as SoldRented).installByInpress;
          this.executionCollapsed = !this.isNotInstalledByInpress
            ? true
            : this.executionCollapsed;
        }
      }
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.tourUnassigned != null) {
      this.tourUnassigned.complete();
      this.tourUnassigned = null;
    }
  }

  private async getNotExecutedHistoryItem(): Promise<void> {
    this.notExecutedHistoryItem =
      await this.getMostRecentNotExecutedHistoryItemControllerRequestService.getLatestNotExecutedHistoryItem(
        this.subProject.id
      );
    if (this.notExecutedHistoryItem != null) {
      if (!this.notExecutedHistoryItem.handled) {
        this.executionCollapsed = false;
      }
      if (this.notExecutedHistoryItem.assets != null) {
        this.projectDisplayObservableService
          .getNotExecutedHistoryItemAssetsFetchedSubscribable()
          .next({
            subProjectId: this.subProject.id,
            assets: this.notExecutedHistoryItem.assets,
          });
      }
    }
  }

  toggleCollapse(): void {
    this.executionCollapsed = !this.executionCollapsed;
  }

  delayedChanged(delayed: boolean): void {
    if (delayed) {
      this.subProject.tourItem = null;
      this.assignSubProjectToTourControllerRequestService
        .unassignSubProjectFromTour(this.subProject.id)
        .then(() =>
          this.projectDisplayObservableService
            .getTourConfigurationChangedSubscribable()
            .next({
              subProjectId: this.subProject.id,
              assignedToTour: false,
              placer: null,
              date: null,
            })
        );
      this.tourUnassigned.next();
    }
    this.save.emit();
  }

  impersonateUser(user: User): void {
    if (user.id != null) {
      const userName = StringHelper.join([user.firstName, user.lastName], ' ');
      this.modalService.confirmDialog(
        `${userName} nabootsen`,
        `Bent u zeker dat u ${userName} wilt nabootsen?`,
        (b: boolean) => {
          if (b) {
            this.authService.impersonateUser({ id: user.id });
          }
        }
      );
    }
  }
}
